import { PMSs } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useReducer } from 'react'

const { disconnected, ...pmsNamesExceptDisconnected } = PMSs // eslint-disable-line no-unused-vars

const defaultLocalStorageKey = 'health-check-filters'

/**
 * Initializes the state for the health check filters.
 * Tries to retrieve from localStorage based on provided key, falling back to default values.
 *
 * @param {string} key - The unique key for storing the filter state in localStorage.
 * @returns {Object} The initial state for the filter.
 */
const getInitialState = key => {
  const savedState = localStorage.getItem(key)
  return savedState
    ? JSON.parse(savedState)
    : {
        hotelGroups: 'all',
        chains: 'all',
        brands: 'all',
        pms: [...Object.values(pmsNamesExceptDisconnected)],
        searchString: '',
        showTelcoDetails: false,
        showLowOutreaches: false,
        showLowConversions: false,
        timeperiod: 30,
        periodStart: moment().add(-31, 'days').format('YYYY-MM-DD'),
        periodEnd: moment().add(-1, 'days').format('YYYY-MM-DD'),
        subsidiaryStatus: [
          'PendingPhoneNumberSetup',
          'PendingCampaignBrandRegistration',
          'BasicAccount',
          'MediumAccount',
          'LargeAccount',
          'AboutToLose'
        ],
        csmIds: []
      }
}

function filterReducer(state, action) {
  switch (action.type) {
    case 'hotelGroups':
      return { ...state, hotelGroups: action.payload }
    case 'chains':
      return { ...state, chains: action.payload }
    case 'brands':
      return { ...state, brands: action.payload }
    case 'pms':
      return { ...state, pms: action.payload }
    case 'searchString':
      return { ...state, searchString: action.payload }
    case 'showTelcoDetails':
      return { ...state, showTelcoDetails: action.payload }
    case 'showLowOutreaches':
      return { ...state, showLowOutreaches: action.payload }
    case 'showLowConversions':
      return { ...state, showLowConversions: action.payload }
    case 'subsidiaryStatus':
      return { ...state, subsidiaryStatus: action.payload }
    case 'timePeriod':
      return {
        ...state,
        timeperiod: action.payload,
        periodStart: moment()
          .add(-action.payload - 1, 'days')
          .format('YYYY-MM-DD'),
        periodEnd: moment().add(-1, 'days').format('YYYY-MM-DD')
      }
    case 'csmIds':
      return { ...state, csmIds: action.payload }
    default:
      throw new Error()
  }
}

/**
 * Custom hook for managing and persisting health check filters.
 *
 * @param {string} keySuffix - Suffix for the localStorage key to create unique keys per instance.
 * @returns {Array} State and dispatch method for the health check filters.
 */
function useHealthCheckFilters(keySuffix) {
  const storageKey = `${defaultLocalStorageKey}:${keySuffix}`
  const [state, dispatch] = useReducer(filterReducer, getInitialState(storageKey))

  // Save to localStorage whenever the state changes
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(state))
  }, [state, storageKey])

  return [state, dispatch]
}

export default useHealthCheckFilters
